import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import './pages/staffpages/staffcss/medicalappointments.css';
import './pages/staffpages/staffcss/peerchats.css';
import './pages/staffpages/staffcss/ResourcesPanel.css';
import { ensureDefaultAdmin } from './services/adminService';
import { LanguageProvider } from './contexts/LanguageContext';
import ErrorBoundary from './components/ErrorBoundary';
import { auth, db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { sessionService } from './services/sessionService';

// Import all pages directly (no lazy loading)
import PersonalInformation from './pages/registration/PersonalInformation';
import ContactDetails from './pages/registration/ContactDetails';
import AccountInformation from './pages/registration/AccountInformation';
import GuardianContactDetails from './pages/registration/GuardianContactDetails';
import AccompanistContactDetails from './pages/registration/AccompanistContactDetails';
import SignupCheckpoint from './pages/registration/SignupCheckpoint';
import UserHome from './pages/userpages/UserHome';
import Login from './pages/login/Login';
import ForgotPassword from './pages/login/ForgotPassword';
import UserVerification from './pages/verification/UserVerification';
import SigninVerification from './pages/verification/SigninVerification';
import AdminVerification from './pages/verification/AdminVerification';
import StaffRoleSelection from './pages/login/StaffRoleSelection';
import StaffVerification from './pages/verification/StaffVerification';
import PasswordVerification from './pages/verification/PasswordVerification';
import UserChangeVerification from './pages/verification/UserChangeVerification';
import ContactChangeOtpPage from './pages/ContactChangeOtpPage';
import ResetPassword from './pages/login/ResetPassword';
import AdminPage from './pages/AdminPage';
import ContentCurator from './pages/staffpages/ContentCurator';
import MedicalProfessional from './pages/staffpages/MedicalProfessional';
import PeerEducator from './pages/staffpages/PeerEducator';
import LandingPage from './pages/LandingPage';
import TermsAndConditions from './pages/TermsAndConditions';
import LearnArticle from './pages/userpages/LearnArticle';
import AdminContentReview from './pages/staffpages/AdminContentReview';
import AdminPeerResourceReview from './pages/staffpages/AdminPeerResourceReview';
import PeerResourceView from './pages/staffpages/PeerResourceView';
import ContentArticleView from './pages/staffpages/ContentArticleView';
import AdminContentRevise from './pages/staffpages/AdminContentRevise';
import ContentRevise from './pages/staffpages/ContentRevise';
import AdminContentRevision from './pages/staffpages/AdminContentRevision';
import ContentViewRevision from './pages/staffpages/ContentViewRevision';
import AppointmentsPanel from './pages/userpages/AppointmentsPanel';

// Auth state listener component
function AuthRedirectHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [hasRedirected, setHasRedirected] = useState(false);

  // ------------------------------------------------------------------
  // Global session-revocation watcher.
  //
  // Attaches a Firestore onSnapshot listener to the current browser's
  // session document as soon as Firebase Auth confirms a signed-in user.
  // If the session is marked revoked (by another device or by the backend
  // logoutAllSessions / revokeSession Cloud Functions), this handler:
  //   1. Clears local session state
  //   2. Signs out the Firebase Auth user
  //   3. Clears localStorage
  //   4. Redirects to /login with an explanatory message
  //
  // This covers pages where ProfileAccounts is NOT mounted — i.e. every
  // route in the app other than the profile page.
  // ProfileAccounts mounts its own identical watcher via watchCurrentSession;
  // having both active is safe because each listener is idempotent and the
  // sign-out path is guarded by the logoutInProgress flag.
  //
  // No polling. One snapshot listener per authenticated session.
  // ------------------------------------------------------------------
  useEffect(() => {
    // Listen for Firebase Auth state changes
    const unsubscribeAuth = auth.onAuthStateChanged((firebaseUser) => {
      if (!firebaseUser) {
        // User signed out — nothing to watch
        return;
      }

      // Attach the session-revocation watcher for this authenticated user.
      // watchCurrentSession returns an unsubscribe function.
      const unsubscribeSession = sessionService.watchCurrentSession(
        firebaseUser.uid,
        async () => {
          // This callback fires when the current session is revoked.
          // Guard against running during an intentional logout.
          const isLogoutInProgress = sessionStorage.getItem('logoutInProgress');
          if (isLogoutInProgress) return;

          sessionStorage.setItem('logoutInProgress', 'true');
          sessionService.clearCurrentSession();

          try {
            await signOut(auth);
          } catch (_) {
            // Sign-out error is non-fatal; continue to clear state
          }

          localStorage.clear();
          sessionStorage.removeItem('freshLoginInProgress');

          navigate('/login', {
            replace: true,
            state: {
              message: 'Your session has been revoked. Please sign in again.',
              type: 'error',
            },
          });

          setTimeout(() => sessionStorage.removeItem('logoutInProgress'), 1000);
        }
      );

      // Return a cleanup function for when onAuthStateChanged fires again
      // (i.e. the user signs out — detach the old session watcher)
      return unsubscribeSession;
    });

    // Clean up the auth listener when the component unmounts
    return () => unsubscribeAuth();
  }, [navigate]); // navigate is stable — this effect runs once on mount

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      // If user is not authenticated, just stop checking
      if (!user) {
        setIsChecking(false);
        setHasRedirected(false);
        sessionStorage.removeItem('logoutInProgress');
        return;
      }

      // Only redirect if we haven't already redirected and we're on landing or login page
      // Check if this is a fresh login (not a returning user) by checking for a flag
      const isFreshLogin = sessionStorage.getItem('freshLoginInProgress');
      const isLogoutInProgress = sessionStorage.getItem('logoutInProgress');
      if (!hasRedirected && !isFreshLogin && !isLogoutInProgress && (location.pathname === '/' || location.pathname === '/login')) {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const accountType = String(userData.accountType || "").toLowerCase();
            const email = user.email?.toLowerCase() || "";
            
            console.log("AuthRedirectHandler - User data:", {
              uid: user.uid,
              email,
              accountType,
              roles: userData.roles,
              role: userData.role
            });
            
            // Determine redirect based on account type and role
            let redirectPath = '/login';
            
            if (accountType === 'admin' || email.endsWith('@admin.auravit.com')) {
              redirectPath = '/admin-management';
            } else if (accountType === 'staff' || email.endsWith('@staff.auravit.com')) {
              // Staff: redirect to first role's dashboard
              const role = userData.role || '';
              const roles = Array.isArray(userData.roles) ? userData.roles : (role ? [role] : []);
              
              console.log("Staff roles detected:", roles);
              
              if (roles.includes('Content Curator')) {
                redirectPath = '/content-curator';
              } else if (roles.includes('Medical Professional')) {
                redirectPath = '/medical-professional';
              } else if (roles.includes('Peer Educator')) {
                redirectPath = '/peer-educator';
              } else {
                // No known roles, go to role selection
                console.log("No known staff roles, going to role selection");
                redirectPath = '/staff-role-selection';
              }
            } else {
              // Regular user
              console.log("Not staff or admin, treating as regular user");
              redirectPath = '/user-home';
            }
            
            // Mark as redirected and navigate
            setHasRedirected(true);
            navigate(redirectPath, { replace: true });
          }
        } catch (error) {
          console.error("Error fetching user data for redirect:", error);
        }
      }
      setIsChecking(false);
    });

    return () => unsubscribe();
  }, [navigate, location.pathname, hasRedirected]);

  // Reset hasRedirected when navigating away from landing or login page
  useEffect(() => {
    if (location.pathname !== '/' && location.pathname !== '/login') {
      setHasRedirected(false);
    }
  }, [location.pathname]);

  // Don't render anything while checking auth state on landing or login page
  const isLogoutInProgress = sessionStorage.getItem('logoutInProgress');
  if (isChecking && !isLogoutInProgress && (location.pathname === '/' || location.pathname === '/login')) {
    return null;
  }

  return null;
}

function App() {
  useEffect(() => {
    ensureDefaultAdmin().catch((err) => {
      console.error("Default admin init failed:", err);
    });
    // Ensure scroll is enabled on app load
    document.body.style.overflow = "auto";
  }, []);

  return (
    <ErrorBoundary>
      <LanguageProvider>
        <Router>
          <AuthRedirectHandler />
          <link
            href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap"
            rel="stylesheet"
          />
          <div className="App">
            <Routes>
              {/* Main Route */}
              <Route path="/" element={<LandingPage />} />

              <Route path="/personal-info" element={<PersonalInformation />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />

              {/* Step Routes */}

              <Route path="/contact-details" element={<ContactDetails />} />
              <Route path="/account-setup" element={<AccountInformation />} />

              <Route path="/login" element={<Login />} />
              <Route path="/forgotpass" element={<ForgotPassword />} />
              <Route path="/password-verification" element={<PasswordVerification />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              <Route path="/user-verification" element={<UserVerification />} />
              <Route path="/signin-verification" element={<SigninVerification />} />
              <Route path="/user-change-verification" element={<UserChangeVerification />} />
              <Route path="/contact-change-otp" element={<ContactChangeOtpPage />} />
              <Route path="/admin-verification" element={<AdminVerification />} />
              <Route path="/staff-role-selection" element={<StaffRoleSelection />} />
              <Route path="/staff-verification" element={<StaffVerification />} />

              <Route path="/guardian-contact-details" element={<GuardianContactDetails />} />
              <Route path="/accompanist-contact-details" element={<AccompanistContactDetails />} />
              <Route path="/signup-checkpoint" element={<SignupCheckpoint />} />

              <Route path="/user-home" element={<UserHome />} />

              <Route path="/admin-management" element={<AdminPage />} />
              <Route path="/admin-page" element={<AdminPage />} />

              <Route path="/content-curator" element={<ContentCurator />} />
              <Route path="/medical-professional" element={<MedicalProfessional />} />
              <Route path="/peer-educator" element={<PeerEducator />} />
              <Route path="/learn/article/:id" element={<LearnArticle />} />
              <Route path="/admin/content-review/:approvalId" element={<AdminContentReview />} />
              <Route path="/admin/revise-content/:approvalId" element={<AdminContentRevise />} />
              <Route path="/curator-revise/:approvalId" element={<ContentRevise />} />
              <Route path="/revision-content/:contentId" element={<AdminContentRevision />} />
              <Route path="/view-revisions/:contentId" element={<ContentViewRevision />} />
              <Route path="/admin/resource-review/:resourceId" element={<AdminPeerResourceReview />} />
              <Route path="/resource-view/:resourceId" element={<PeerResourceView />} />
              <Route path="/article-view" element={<ContentArticleView />} />
              <Route path="/appointments" element={<AppointmentsPanel />} />
            </Routes>
        </div>
      </Router>
    </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
